import React from "react";
import { animated } from "react-spring";
import { Box, List, ListItem, ListItemIcon, ListItemText, makeStyles } from "@material-ui/core";
import clsx from "clsx";

import { ArrowRight as ArrowRightIcon } from "@eroad/icons/material";

import type { CoretexTheme } from "../../theme";
import type { MenuItem } from "./types";

const AnimatedBox = animated(Box);
const subMenuWidth = 220;
const useStyles = makeStyles((theme: CoretexTheme) => ({
  baseListItem: {
    height: "56px",
    padding: theme.spacing(4, 0),
    color: theme.palette.mono.white,
    fill: theme.palette.mono.white,
    "&:hover": { backgroundColor: theme.palette.mono.ultraDark },
  },
  subMenuItem: {
    height: "38px",
    padding: theme.spacing(2, 4),
    backgroundColor: theme.palette.mono.ultraDark,
    "&:hover > * > span": {
      backgroundColor: theme.palette.mono.primaryDark,
      borderRadius: "5px",
    },
    "& > * > span": {
      padding: theme.spacing(2),
    },
  },
  text: {
    padding: theme.spacing(2),
  },
  subIcon: {
    fontSize: "small",
  },
  active: {
    position: "relative",
    backgroundColor: `${theme.palette.mono.ultraDark} !important`,
    "&:hover": { backgroundColor: `${theme.palette.mono.ultraDark} !important` },
    "&::before": {
      display: "block",
      position: "absolute",
      content: '""',
      top: 0,
      left: 0,
      backgroundColor: theme.palette.colour.orange,
      height: "100%",
      width: "4px",
    },
  },
  icon: {
    "& > *": {
      "& > *": {
        fill: theme.palette.mono.secondaryDark,
      },
      margin: "0 auto",
    },
  },
  iconSelected: {
    "& > *": {
      "& > *": {
        fill: theme.palette.mono.white,
      },
      margin: "0 auto",
    },
  },
  animatedBox: {
    width: subMenuWidth,
    position: "fixed",
    top: 0,
    minHeight: "100vh",
    maxHeight: "100vh",
    backgroundColor: theme.palette.mono.ultraDark,
    color: theme.palette.mono.white,
    display: "flex",
    flexDirection: "column",
  },
  bottomSubMenu: {
    marginTop: "auto",
    marginBottom: theme.spacing(2),
  },
  ArrowRightIcon: {
    color: theme.palette.mono.white,
  },
  list: {
    "&:hover": {
      "& > $animatedBox": {
        visibility: "visible",
        transitionDelay: "0s",
      },
    },
    "& > $animatedBox": {
      visibility: "hidden",
      transition: "visibility 0s",
      transitionDelay: "0.2s",
    },
  },
}));

interface MenuItemProps {
  menuItem: MenuItem;
  menuLevel: number;
  expanded: string[];
  setExpanded: (item: string) => void;
  selected: boolean;
  offset: number;
  bottomSubMenu?: boolean;
}

const MenuListItem = ({
  menuItem,
  menuLevel,
  expanded,
  setExpanded,
  selected,
  offset,
  bottomSubMenu,
}: MenuItemProps): JSX.Element => {
  const classes = useStyles();
  const { text, icon, subMenuItems, url, ...menuItemRest } = menuItem;
  const hasSubMenu = subMenuItems && subMenuItems.length > 0;
  const subMenuLevel: number = menuLevel ? menuLevel + 1 : 1;

  const ListItemLink = (props: any): React.ReactElement => {
    if (menuItem.component) return <ListItem button component={menuItem.component} {...props} />;
    return <ListItem button component={hasSubMenu ? "div" : "a"} {...props} />;
  };

  return (
    <ListItemLink
      key={`nav-${menuLevel}-${text}`}
      button
      disableGutters={subMenuItems && subMenuItems.length > 0}
      selected={selected}
      classes={{ selected: classes.active }}
      className={clsx(classes.list, subMenuLevel < 3 && classes.baseListItem, subMenuLevel > 2 && classes.subMenuItem)}
      href={url}
      to={menuItem.url}
      {...menuItemRest}
    >
      {subMenuLevel < 3 && (
        <>
          <ListItemIcon data-testid={`nav-${text}`} className={selected ? classes.iconSelected : classes.icon}>
            {icon}
          </ListItemIcon>
          <ListItemText primary={text} />
        </>
      )}
      {subMenuLevel > 2 && <ListItemText primary={text} />}
      {hasSubMenu && <ArrowRightIcon className={classes.ArrowRightIcon} />}
      {menuItem.subMenuItems && menuItem.subMenuItems.length > 0 && (
        <AnimatedBox className={classes.animatedBox} style={{ left: offset }}>
          <List component="div" className={clsx(bottomSubMenu && classes.bottomSubMenu)}>
            {menuItem.subMenuItems.map((subMenuItem: MenuItem) => (
              <MenuListItem
                key={`sub-${subMenuLevel}-${subMenuItem.url}`}
                menuItem={subMenuItem}
                menuLevel={subMenuLevel}
                expanded={expanded}
                setExpanded={setExpanded}
                selected={false}
                offset={offset + subMenuWidth}
              />
            ))}
          </List>
        </AnimatedBox>
      )}
    </ListItemLink>
  );
};

export default MenuListItem;
